import React, {createContext, useEffect, useState} from 'react'
import toast from 'react-hot-toast'
import {EntityWithIdAndDescription, EntityWithIdAndDescriptionFields, EntityWithIdFields} from 'src/types'
import {HttpMessages} from 'src/http/workflow/httpMessages'
import {MensajeDetalle, MensajeDetalleFields} from '../messages/types'
import {Navigate, Outlet, RouteObject, useNavigate, useParams} from 'react-router-dom'
import {useMutation} from '@tanstack/react-query';

import {z} from 'src/lib'
import {SistemaWRCredito} from './sistemas/wrc/SistemaWRCredito'
import {Documentacion} from './sistemas/Documentacion'
import {Comentarios} from './sistemas/Comentarios'
import {NuevoSistema} from './NuevoSistema'
import {SistemaWRMercado} from './sistemas/wrm'
import {useAcciones} from './hooks/useAcciones'
import {useEjecutarAccion} from './hooks/useEjecutarAccion'
import {SistemaContraparte} from './sistemas/contraparte/SistemaContraparte'
import {LoaderProvider} from 'src/context/LoaderContext'
import {useLoader} from 'src/hooks/useLoader'
import SistemaCLGarantia from "./sistemas/clg/SistemaCLGarantia";
import OperacionLayout from './layout/OperacionLayout'
import ItaptecSpinner from "src/layouts/components/ItaptecSpinner";
import {HttpCLG} from "./sistemas/clg/api/HttpCLG";
import {downloadFileBlobHelper} from "../../utils/helpers";
import {HttpContraparte} from "./sistemas/contraparte/api/httpContraparte";
import {HttpWRC} from "./sistemas/wrc/api";
import {HttpWRM} from "./sistemas/wrm/api";
import SistemaCLFB from "./sistemas/clfb/SistemaCLFB";
import {SistemaCorporate} from "./sistemas/corporate/SistemaCorporate";
import {HttpCorporate} from "./sistemas/corporate/api/httpCorporate";
import {HttpCLFB} from "./sistemas/clfb/api/HttpCLFB";
import SistemaCapitalMarkets from "./sistemas/capital_markets/SistemaCapitalMarkets";
import {HttpCapitalMarkets} from "./sistemas/capital_markets/api/httpCapitalMarkets";
import {SistemaNotaEstructurada} from "./sistemas/nota_estructurada/SistemaNotaEstructurada";
import {SistemaTipo} from "../../types/enum/enum";
import {HttpNotaEstructurada} from "./sistemas/nota_estructurada/api/httpNotaEstructurada";
import SistemaTransferenciaTerceros from "./sistemas/transferencia-terceros/SistemaTransferenciaTerceros";
import {HttpTransferenciaTerceros} from "./sistemas/transferencia-terceros/api/httpTransferenciaTerceros";
import SistemaCorporateLending from "./sistemas/corporate_lending/SistemaCorporateLending";
import {HttpCorporateLending} from "./sistemas/corporate_lending/api/httpCorporateLending";
import SistemaControlOperativo from "./sistemas/control-operativo/SistemaControlOperativo";
import {HttpControlOperativo} from "./sistemas/control-operativo/api/httpControlOperativo";
import {HttpLEP} from './sistemas/lep/api/httpLEP'
import SistemaLEP from "./sistemas/lep/SistemaLEP";
import SistemaLCLS from "./sistemas/lcls/SistemaLCLS";
import {HttpLCLS} from "./sistemas/lcls/api/httpLCLS";
import SistemaLCTC from "./sistemas/lctc/SistemaLCTC";
import {HttpLCTC} from "./sistemas/lctc/api/httpLCTC";
import { PermissionType } from 'src/types/security'
import SistemaAFOPU from './sistemas/afopu/SistemaAFOPU'
import { HttpAFOPU } from './sistemas/afopu/api/httoAFOPU'
import SistemaAFOCL from './sistemas/afocl/SistemaAFOCL'
import { HttpAFOCL } from './sistemas/afocl/api/httoAFOCL'
import SistemaLCSLHPTI from './sistemas/lcslhpti/SistemaLCSLHPTI'
import { HttpLCSLHPTI } from './sistemas/lcslhpti/api/httpLCSLSHPTI'
import { HttpNOTES } from './sistemas/notes/api/httpNOTES'
import SistemaNOTES from './sistemas/notes/SistemaNOTES'
import { HttpADCM } from './sistemas/adcm/api/httpADCM'
import SistemaADCM from './sistemas/adcm/SistemaADCM'
import SistemaADL from './sistemas/adl/SistemaADL'
import { HttpADL } from './sistemas/adl/api/httpADL'

export const rutaPorCodSistema = {
    [SistemaTipo.MinutaCredito]: 'wrc',
    [SistemaTipo.MinutaMercado]: 'wrm',
    [SistemaTipo.Contrapartes]: 'contrapartes',
    [SistemaTipo.CLG]: 'clg',
    [SistemaTipo.CLFB]: 'clfb',
    [SistemaTipo.Corporate]: 'corporate',
    [SistemaTipo.CapitalMarkets]: 'capital-markets',
    [SistemaTipo.NotaEstructurada]: 'nota-estructurada',
    [SistemaTipo.TransferenciaTerceros]: 'transferencia-terceros',
    [SistemaTipo.CorporateLending]: 'corporate-lending',
    [SistemaTipo.ControlOperativo]: 'control-operativo',
    [SistemaTipo.LEP]: 'lep',
    [SistemaTipo.LCLS]: 'lcls',
    [SistemaTipo.LCTC]: 'lctc',
    [SistemaTipo.AFOPU]: 'afopu',
    [SistemaTipo.AFOCL]: 'afocl',
    [SistemaTipo.LCSLHPTI]: 'lcslhpti',
    [SistemaTipo.NOTES]: 'notes',
    [SistemaTipo.ADCM]: 'adcm',
    [SistemaTipo.ADL]: 'adl'
} as const

const urlSchemaIdMensaje = z.object({
    idMensaje: z.coerce.number()
})

const cargarMensaje = (parametros: unknown, codSistema: SistemaTipo): Promise<MensajeDetalle> => {

    const parsed = urlSchemaIdMensaje.safeParse(parametros)

    if (!parsed.success) {
        return Promise.reject('Id mensaje inválido.')
    }

    return HttpMessages.getDetail(parsed.data.idMensaje)
        .then(value => {
            if (value[MensajeDetalleFields.CodSistemaTipo] === codSistema) {
                return Promise.resolve(value)
            } else {
                return Promise.reject('Error grave: este mensaje no pertenece a este sistema.')
            }
        })
        .catch(e => {
            console.log('error al cargar mensaje: ', e)

            return Promise.reject(e) // TODO: acá validar el tipo del error, no autorizado / no existe, etc
        })
}


// # region nuevo
// ESTO SIRVE PARA EL NUEVO

// const urlParamsNuevaOperacionSchema = z.object({
//     codSistema: z.coerce.number(),          // TODO: agregar que esté dentro de los posibles sistemas con z.enum
//     codSistemaPrincipal: z.coerce.number()  // TODO: idem arriba
// })

// type UrlParamsNuevaOperacion = z.infer<typeof urlParamsNuevaOperacionSchema>

// const cargarParametros: LoaderFunction = ({ request, params }): Promise<UrlParamsNuevaOperacion> => {
//     const url = new URL(request.url);
//     const stringSistemaPrincipal = url.searchParams.get(PROJECT_CONFIG.alias.sistemaPrincipal)  // nombre pais
//     const stringSistema = url.searchParams.get(PROJECT_CONFIG.alias.sistema)                    // nombre operacion

//     // parsear, validar y devolver codSistemaPrincipal y codSistema

//     // TODO: esto obviamente está hardcodeado
//     return Promise.resolve({
//         codSistema: 1,
//         codSistemaPrincipal: 1
//     })
// }
// # endregion

const WrapperPaginaOperacion = ({codSistema}: { codSistema: SistemaTipo }) => {

    // const loaderData = useLoaderData()

    // console.log(loaderData)

    const [mensaje, setMensaje] = useState<MensajeDetalle>()

    const params = useParams()

    const navigation = useNavigate()

    useEffect(() => {
        cargarMensaje(params, codSistema)
            .then(msj => setMensaje(msj))

            .catch(() => navigation('/home'))
    }, [])

    if (!mensaje) {

        return <ItaptecSpinner/> // TODO: esto no deberías ser 404, sino 400 (bad request)
    }

    return (
        <PaginaOperacion mensaje={mensaje}>
            <Outlet/>
        </PaginaOperacion>
    )
}

export type ContextOperacionType = {
    setSavePromise: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
    setPromiseInformeContent?: React.Dispatch<React.SetStateAction<(() => Promise<JSX.Element>) | undefined>>
    idOperacion: number
    codSistemaTipo: number
    codSistemaPrincipal: number,
    codSistema: number,
    permisoTipo: PermissionType,
}


export const ContextOperacion = createContext<ContextOperacionType>({
    setSavePromise: console.log,
    idOperacion: 0,
    codSistemaTipo: 0,
    codSistemaPrincipal: 0,
    codSistema: 0,
    permisoTipo: PermissionType.None
});


const getInformePromise = (mensaje: MensajeDetalle) => {

    switch (mensaje[MensajeDetalleFields.CodSistemaTipo]) {
        case SistemaTipo.MinutaCredito:
            return HttpWRC.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.MinutaMercado:
            return HttpWRM.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.Contrapartes:
            return HttpContraparte.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.CLG:
            return HttpCLG.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.CLFB:
            return HttpCLFB.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.Corporate:
            return HttpCorporate.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.CapitalMarkets:
            return HttpCapitalMarkets.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.NotaEstructurada:
            return HttpNotaEstructurada.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.TransferenciaTerceros:
            return HttpTransferenciaTerceros.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.CorporateLending:
            return HttpCorporateLending.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.ControlOperativo:
            return HttpControlOperativo.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.LEP:
            return HttpLEP.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.LCLS:
            return HttpLCLS.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.LCTC:
            return HttpLCTC.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.AFOPU:
            return HttpAFOPU.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.AFOCL:
            return HttpAFOCL.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.LCSLHPTI:
            return HttpLCSLHPTI.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.NOTES:
            return HttpNOTES.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.ADCM:
            return HttpADCM.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        case SistemaTipo.ADL:
            return HttpADL.descargarInforme(mensaje[MensajeDetalleFields.IdOperacion]);
        default:
            return Promise.reject(`Error: sistema ${mensaje[MensajeDetalleFields.CodSistemaTipo]} no reconocido.`)
    }

}

const PaginaOperacion = ({mensaje, children}: { mensaje: MensajeDetalle, children: React.ReactNode }) => {

    const [promiseInformeContent, setPromiseInformeContent] = useState<() => Promise<JSX.Element>>()

    const [savePromise, setSavePromise] = useState<() => void>()   // TODO: funciones default para que

    const navigation = useNavigate()
    const {showLoader, hideLoader} = useLoader()

    const {acciones} = useAcciones(mensaje[MensajeDetalleFields.IdEtapaActual])
    const {onClickEjecutar} = useEjecutarAccion(mensaje[EntityWithIdFields.Id])

    const descargaInforme = useMutation({
        mutationFn: () => {
            showLoader()

            return getInformePromise(mensaje);
        },
        onSuccess: (res) => {
            hideLoader()
            downloadFileBlobHelper(res)
        },
        onError: () => {
            hideLoader()
        },
    });


    // TODO: mejorar las promesas para que tengan mensaje de error. Mejorar el tipo.
    const handleEnviar = (seleccion: EntityWithIdAndDescription) => {
        showLoader()
        const promise = onClickEjecutar({
            idAccion: seleccion[EntityWithIdAndDescriptionFields.Id]
        })

        toast.promise(promise, {
            loading: 'Enviando.',
            success: 'Enviado correctamente.',
            error: e => e
        })
            .then(handleCloseMessage)
            .catch(hideLoader)
    }

    const handleCloseMessage = () => {
        showLoader();
        const promise = HttpMessages.closeMessage(mensaje[EntityWithIdFields.Id])

        toast.promise(promise, {
            loading: 'Liberando la operación.',
            success: 'Operación liberada.',
            error: 'Error al liberar la operación'
        })
            .then(() => {
                setTimeout(() => navigation('/'), 500)

            })
            .catch(() => {
                hideLoader()
                toast.error("Error al liberar el mensaje.")
            })
    }

    const handleDescargarInforme = () => {
        const promise = descargaInforme.mutateAsync()
        toast.promise(promise, {
            loading: 'Generando informe.',
            success: 'Informe listo para descarga.',
            error: e => `${e}`
        })
    }

    const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false)

    return (
        <LoaderProvider>
            <OperacionLayout

                acciones={{
                    isNewOperacion: false,
                    saveHandler: savePromise,
                    closeHandler: handleCloseMessage,
                    printHandler: (handleDescargarInforme),
                    actionsOptions: mensaje[MensajeDetalleFields.PermisoTipo] > 1 ? acciones : [],
                    actionsHandler: handleEnviar
                }}
                message={mensaje}
            >
                <ContextOperacion.Provider value={{
                    setSavePromise,
                    setPromiseInformeContent,
                    idOperacion: mensaje[MensajeDetalleFields.IdOperacion],
                    codSistemaTipo: mensaje[MensajeDetalleFields.CodSistemaTipo],
                    codSistemaPrincipal: mensaje[MensajeDetalleFields.CodigoSistemaPrincipal],
                    codSistema: mensaje[MensajeDetalleFields.CodigoSistema],
                    permisoTipo: mensaje[MensajeDetalleFields.PermisoTipo]
                }}>
                    {children}
                </ContextOperacion.Provider>
            </OperacionLayout>
        </LoaderProvider>
    )
}

// TODO: problema, los loaders cargan antes de que se rendericen las rutas, entonces el check que hago
// de si el user está auth antes de renderizar estas rutas es al dope, se rompe todo. Entonces le
// pongo un condicional de que hay loader SI el user está auth, lo cual es redundante, porque el
// objetivo era hacerlo en el router principal... Por ahora anda, pero queda feo y habría que
// encontrar la solución. Luca.

const rutasDeOperaciones: Record<SistemaTipo, RouteObject> = {
    [SistemaTipo.MinutaCredito]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.MinutaCredito]}/:idMensaje`,

                    // loader: (async ({ params }) => cargarMensaje(params, MensajeTipo.MinutaCredito)),   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.MinutaCredito}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaWRCredito/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.MinutaMercado]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.MinutaMercado]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.MinutaMercado)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.MinutaMercado}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaWRMercado/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.Contrapartes]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.Contrapartes]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.Contrapartes)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.Contrapartes}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaContraparte/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.CLG]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.CLG]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.CLG}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaCLGarantia/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.CLFB]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.CLFB]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.CLFB}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaCLFB/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.Corporate]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.Corporate]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.Corporate}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaCorporate/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.CapitalMarkets]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.CapitalMarkets]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.CapitalMarkets}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaCapitalMarkets/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.NotaEstructurada]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.NotaEstructurada]}/:idMensaje`,

                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.NotaEstructurada}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaNotaEstructurada/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.TransferenciaTerceros]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.TransferenciaTerceros]}/:idMensaje`,

                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.TransferenciaTerceros}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaTransferenciaTerceros/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.CorporateLending]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.CorporateLending]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.CorporateLending}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaCorporateLending/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.ControlOperativo]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.ControlOperativo]}/:idMensaje`,

                    // loader: userStorage.hasToken() ? (async ({ params }) => cargarMensaje(params, MensajeTipo.CLG)) : undefined,   // TODO: pasar a promises
                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.ControlOperativo}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaControlOperativo/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.LEP]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.LEP]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.LEP}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaLEP/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.LCLS]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.LCLS]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.LCLS}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaLCLS/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
    [SistemaTipo.LCTC]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.LCTC]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.LCTC}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaLCTC/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.AFOPU]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.AFOPU]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.AFOPU}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaAFOPU/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.AFOCL]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.AFOCL]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.AFOCL}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaAFOCL/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.LCSLHPTI]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.LCSLHPTI]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.LCSLHPTI}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaLCSLHPTI/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.NOTES]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.NOTES]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.NOTES}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaNOTES/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.ADCM]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.ADCM]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.ADCM}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaADCM/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
        [SistemaTipo.ADL]:
        {
            children: [
                {
                    path: `${rutaPorCodSistema[SistemaTipo.ADL]}/:idMensaje`,


                    element: <WrapperPaginaOperacion codSistema={SistemaTipo.ADL}/>,

                    // errorElement: <Error404 />,
                    children: [
                        {
                            path: '',
                            element: <SistemaADL/>
                        },
                        {
                            path: 'documentos',
                            element: <Documentacion/>
                        },
                        {
                            path: 'comentarios',
                            element: <Comentarios/>
                        }
                    ]
                },
            ]
        },
}

export const sistemaRouter: RouteObject[] = [
    {
        path: '/operacion',
        element: <Outlet/>,
        children: [
            rutasDeOperaciones[SistemaTipo.MinutaCredito],
            rutasDeOperaciones[SistemaTipo.MinutaMercado],
            rutasDeOperaciones[SistemaTipo.Contrapartes],
            rutasDeOperaciones[SistemaTipo.CLG],
            rutasDeOperaciones[SistemaTipo.CLFB],
            rutasDeOperaciones[SistemaTipo.Corporate],
            rutasDeOperaciones[SistemaTipo.CapitalMarkets],
            rutasDeOperaciones[SistemaTipo.NotaEstructurada],
            rutasDeOperaciones[SistemaTipo.TransferenciaTerceros],
            rutasDeOperaciones[SistemaTipo.CorporateLending],
            rutasDeOperaciones[SistemaTipo.ControlOperativo],
            rutasDeOperaciones[SistemaTipo.LEP],
            rutasDeOperaciones[SistemaTipo.LCLS],
            rutasDeOperaciones[SistemaTipo.LCTC],
            rutasDeOperaciones[SistemaTipo.AFOPU],
            rutasDeOperaciones[SistemaTipo.AFOCL],
            rutasDeOperaciones[SistemaTipo.LCSLHPTI],
            rutasDeOperaciones[SistemaTipo.NOTES],
            rutasDeOperaciones[SistemaTipo.ADCM],
            rutasDeOperaciones[SistemaTipo.ADL],
            
            {path: '', element: <Navigate to='/home'/>}
        ],
    },
    {
        path: '/operacion/nuevo',
        element: <NuevoSistema/>   // TODO: acá en nuevo agregar el dialog y eso así se puede usar también
    }
]

